import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import { createStore, useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type {
  ChartDateRange,
  HeatmapViewPreference,
  NewPortfolio,
  Portfolio,
  PortfoliosResponse,
  UserPreference,
  Watchlist,
  WatchlistComparisonData,
  WatchlistComparisonInfo,
  WatchlistResponse,
  WatchlistsSummaryResponse,
} from "@bloomberg/octagon-shared";
import { watchlistClient } from "~config/octagon-client";
import { logError } from "~utils/newrelic/newrelicBrowser";
import { SECURITY_TYPE_TO_ASSET_TYPE } from "~constants";

// Octagon client instance
const getOctagonClient = () => watchlistClient.getInstance();

// Define your watchlist state shape
export interface WatchlistState {
  // User state
  userId: string | null;

  // Watchlists data
  portfolios: Portfolio[];
  currentWatchlist: Watchlist | null;
  selectedPortfolioId: number | null;

  // User preferences
  userPreferences: UserPreference | null;

  // UI state
  isLoading: boolean;
  error: string | null;
  sortBy: "name" | "ticker" | "price" | "change";
  sortDirection: "asc" | "desc";
  filterQuery: string;
  selectedSecurityTypes: string[];
  selectedSectors: string[];

  // Comparison chart state
  comparisonData: WatchlistComparisonData | null;
  comparisonSettings: WatchlistComparisonInfo | null;
  selectedChartRange: ChartDateRange;
  comparisonChartLoading: boolean;
  comparisonChartError: string | null;
  selectedChartLegends: Set<string>;
  isComparisonMaximized: boolean;

  // Heatmap state
  // Heatmap maximized state (mutually exclusive with isComparisonMaximized - governed by dashboardView)
  isHeatmapMaximized: boolean;
  heatmapTabView: HeatmapViewPreference;
  isDrawerOpen: boolean;
  selectedTicker: string | null;

  // Cached computed data
  securityTypeData: Array<{
    name: string;
    id: string;
    percentage: number;
    color: string;
    count: number;
  }>;
  sectorData: string[];
}

// Actions to mutate state
export interface WatchlistActions {
  // Initialize
  initialize: (userId: string) => Promise<void>;

  // Portfolio/Watchlist operations
  fetchPortfolios: () => Promise<void>;
  selectPortfolio: (portfolioId: number) => Promise<void>;
  refreshCurrentWatchlist: () => Promise<void>;

  // User preferences
  fetchUserPreferences: () => Promise<void>;
  updateUserPreferences: (
    preferences: Partial<UserPreference>
  ) => Promise<void>;

  // UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSortBy: (sortBy: WatchlistState["sortBy"]) => void;
  setSortDirection: (direction: "asc" | "desc") => void;
  setFilterQuery: (query: string) => void;
  setSelectedSecurityTypes: (types: string[]) => void;
  setSelectedSectors: (sectors: string[]) => void;

  // Watchlist CRUD
  saveWatchlistChanges: (data: Partial<NewPortfolio>) => Promise<void>;
  deleteWatchlist: () => Promise<void>;

  // Comparison chart operations
  fetchComparisonChart: (chartRange?: ChartDateRange) => Promise<void>;
  fetchComparisonSettings: () => Promise<void>;
  updateComparisonSettings: (
    comparisons: WatchlistComparisonInfo["comparisons"]
  ) => Promise<void>;
  setChartRange: (range: ChartDateRange) => void;
  toggleChartLegend: (tickerId: string) => void;
  setSelectedLegends: (tickerIds: string[]) => void;
  clearComparisonChart: () => void;
  toggleComparisonMaximized: () => void;

  // Heatmap operations
  toggleHeatmapMaximized: () => void;
  setHeatmapTab: (tab: HeatmapViewPreference) => void;
  openDrawer: (ticker: string) => void;
  closeDrawer: () => void;

  // Utility
  reset: () => void;
}

// Combined store type
type WatchlistStore = WatchlistState & WatchlistActions;

// Initial state
const createInitialState = (): WatchlistState => ({
  userId: null,
  portfolios: [],
  currentWatchlist: null,
  selectedPortfolioId: null,
  userPreferences: null,
  isLoading: true,
  error: null,
  sortBy: "ticker",
  sortDirection: "asc",
  filterQuery: "",
  selectedSecurityTypes: [],
  selectedSectors: [],
  // Comparison chart state
  comparisonData: null,
  comparisonSettings: null,
  selectedChartRange: "1M",
  comparisonChartLoading: false,
  comparisonChartError: null,
  selectedChartLegends: new Set(),
  isComparisonMaximized: false,
  // Heatmap state
  isHeatmapMaximized: false,
  heatmapTabView: "PERF",
  isDrawerOpen: false,
  selectedTicker: null,
  // Cached computed data
  securityTypeData: [],
  sectorData: [],
});

// Helper function to compute security type data
const computeSecurityTypeData = (watchlist: Watchlist | null) => {
  if (!watchlist?.positions || watchlist.positions.length === 0) {
    return [];
  }

  const securityCount = watchlist.positions.length;

  type SecurityTypeName =
    | "Bond"
    | "Commodity"
    | "Currency"
    | "Fund"
    | "Index"
    | "Stock";
  type SecurityTypeCount = Record<SecurityTypeName, number>;

  const securityTypeCount = watchlist.positions.reduce<SecurityTypeCount>(
    (acc, position) => {
      const secType =
        position.marketData?.terminalSecurityType ||
        position.marketData?.securityType;

      if (!secType) {
        return acc;
      }

      const assetType = (SECURITY_TYPE_TO_ASSET_TYPE[secType] ||
        secType) as SecurityTypeName;

      return { ...acc, [assetType]: (acc[assetType] || 0) + 1 };
    },
    {
      Bond: 0,
      Commodity: 0,
      Currency: 0,
      Fund: 0,
      Index: 0,
      Stock: 0,
    }
  );

  const sortedTypes = Object.entries(securityTypeCount)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const COLOR_GRADIENTS = [
    "#FF008C",
    "#009D9A",
    "#9757FF",
    "#AE8E00",
    "#97542B",
    "#949494",
  ];

  return sortedTypes.map(([name, count], index) => ({
    name,
    id: name,
    percentage: (count / securityCount) * 100,
    color: COLOR_GRADIENTS[index] || COLOR_GRADIENTS[5],
    count,
  }));
};

// Aggregates sector names from positions, sorted by frequency (descending)
const computeSectorData = (watchlist: Watchlist | null): string[] => {
  if (!watchlist?.positions || watchlist.positions.length === 0) {
    return [];
  }

  const sectors = new Set<string>();

  for (const position of watchlist.positions) {
    const sector = position.marketData?.sector;

    if (sector) {
      sectors.add(sector);
    }
  }

  return Array.from(sectors).sort((a, b) => a.localeCompare(b));
};

// Create the Zustand store
const createWatchlistStore = (initialState?: Partial<WatchlistState>) => {
  return createStore<WatchlistStore>((set, get) => ({
    // Initial state
    ...createInitialState(),
    ...initialState,

    // Initialize with user
    initialize: async (userId: string) => {
      set({ userId, isLoading: true, error: null });

      try {
        const client = getOctagonClient();

        // Fetch user preferences, portfolios, and default watchlist in parallel
        const [prefsResponse, portfoliosResponse, summaryResponse] =
          await Promise.all([
            client.getUserPreference(userId),
            client.getPortfolios(userId),
            client.getWatchlistSummary(userId),
          ]);

        const userPreferences = prefsResponse as Required<UserPreference>;
        const portfoliosData = portfoliosResponse as PortfoliosResponse;
        const summaryData = summaryResponse as WatchlistsSummaryResponse;

        // Sync isComparisonMaximized with dashboardView on initial load
        const isComparisonMaximized = userPreferences?.dashboardView === "COMP";

        // Sync isHeatmapMaximized with dashboardView on initial load
        const isHeatmapMaximized = userPreferences?.dashboardView === "HTMP";

        // Initialize heatmapTabView from user preferences
        const heatmapTabView = userPreferences?.webHeatmapView || "PERF";

        const defaultWatchlist = summaryData.defaultWatchlist || null;

        set({
          userPreferences,
          portfolios: portfoliosData.portfolios || [],
          currentWatchlist: defaultWatchlist,
          securityTypeData: computeSecurityTypeData(defaultWatchlist),
          sectorData: computeSectorData(defaultWatchlist),
          selectedPortfolioId: summaryData.defaultWatchlist?.id || null,
          isComparisonMaximized,
          isHeatmapMaximized,
          heatmapTabView,
          isLoading: false,
        });
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to initialize watchlist");

        logError(err, { component: "WatchlistContext", action: "initialize" });
        set({ error: err.message, isLoading: false });
      }
    },

    // Fetch all portfolios
    fetchPortfolios: async () => {
      const { userId } = get();

      if (!userId) {
        set({ error: "User ID not set" });

        return;
      }

      set({ isLoading: true, error: null });

      try {
        const client = getOctagonClient();
        const response = await client.getPortfolios(userId);
        const data = response as PortfoliosResponse;

        set({
          portfolios: data.portfolios || [],
          isLoading: false,
        });
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "Failed to fetch portfolios",
          isLoading: false,
        });
      }
    },

    // Select and load a specific portfolio
    selectPortfolio: async (portfolioId: number) => {
      const { userId, currentWatchlist, selectedPortfolioId } = get();

      if (!userId) {
        set({ error: "User ID not set" });

        return;
      }

      // If already selected, don't re-fetch
      if (
        selectedPortfolioId === portfolioId &&
        currentWatchlist?.id === portfolioId
      ) {
        return;
      }

      // Set loading state immediately to show shimmer
      set({
        isLoading: true,
        error: null,
        selectedPortfolioId: portfolioId,
        currentWatchlist: null,
        securityTypeData: [],
        sectorData: [],
        selectedSecurityTypes: [],
        selectedSectors: [],
      });

      try {
        const client = getOctagonClient();
        const response = await client.getWatchlistById(userId, portfolioId);
        const data = response as WatchlistResponse;

        set({
          currentWatchlist: data.watchlist,
          securityTypeData: computeSecurityTypeData(data.watchlist),
          sectorData: computeSectorData(data.watchlist),
          isLoading: false,
        });
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to fetch watchlist");

        logError(err, {
          component: "WatchlistContext",
          action: "selectPortfolio",
          portfolioId,
        });
        set({ error: err.message, isLoading: false });
      }
    },

    // Refresh current watchlist (bypasses selectPortfolio's same-id guard)
    refreshCurrentWatchlist: async () => {
      const { userId, selectedPortfolioId } = get();

      if (!userId || !selectedPortfolioId) {
        set({ error: !userId ? "User ID not set" : "No watchlist selected" });

        return;
      }

      set({ isLoading: true, error: null });

      try {
        const client = getOctagonClient();
        const response = await client.getWatchlistById(
          userId,
          selectedPortfolioId
        );
        const data = response as WatchlistResponse;

        set({
          currentWatchlist: data.watchlist,
          securityTypeData: computeSecurityTypeData(data.watchlist),
          sectorData: computeSectorData(data.watchlist),
        });
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to refresh watchlist");

        logError(err, {
          component: "WatchlistContext",
          action: "refreshCurrentWatchlist",
          selectedPortfolioId,
        });
        set({ error: err.message });
      } finally {
        set({ isLoading: false });
      }
    },

    // Save watchlist metadata + holdings changes
    saveWatchlistChanges: async (data: Partial<NewPortfolio>) => {
      const { userId, selectedPortfolioId } = get();

      if (!userId || !selectedPortfolioId) {
        throw new Error("User ID or watchlist not set");
      }

      const client = getOctagonClient();

      await client.updateWatchlistById(userId, selectedPortfolioId, data);
      await Promise.all([
        get().refreshCurrentWatchlist(),
        get().fetchPortfolios(),
      ]);
    },

    // Delete the currently selected watchlist
    deleteWatchlist: async () => {
      const { userId, selectedPortfolioId } = get();

      if (!userId || !selectedPortfolioId) {
        throw new Error("User ID or watchlist not set");
      }

      const client = getOctagonClient();

      await client.deletePortfolioById(userId, selectedPortfolioId);

      const response = await client.getPortfolios(userId);
      const remaining = (response as PortfoliosResponse).portfolios ?? [];

      set({ portfolios: remaining });

      if (remaining.length > 0) {
        await get().selectPortfolio(remaining[0].id);
      } else {
        set({
          currentWatchlist: null,
          selectedPortfolioId: null,
          securityTypeData: [],
          sectorData: [],
          selectedSecurityTypes: [],
          selectedSectors: [],
        });
      }
    },

    // Fetch user preferences
    fetchUserPreferences: async () => {
      const { userId } = get();

      if (!userId) {
        set({ error: "User ID not set" });

        return;
      }

      set({ isLoading: true, error: null });

      try {
        const client = getOctagonClient();
        const response = await client.getUserPreference(userId);
        const data = response as Required<UserPreference>;

        set({
          userPreferences: data,
          isLoading: false,
        });
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "Failed to fetch user preferences",
          isLoading: false,
        });
      }
    },

    // Update user preferences
    updateUserPreferences: async (preferences: Partial<UserPreference>) => {
      const { userId, userPreferences } = get();

      if (!userId) {
        set({ error: "User ID not set" });

        return;
      }

      // Optimistic update: apply state changes immediately (before API call)
      // This prevents flashing intermediate states during view transitions
      const optimisticUpdate: Partial<WatchlistState> = {
        userPreferences: { ...userPreferences, ...preferences },
      };

      // If dashboardView is being updated, sync the comparison maximized state
      if (preferences.dashboardView !== undefined) {
        optimisticUpdate.isComparisonMaximized =
          preferences.dashboardView === "COMP";
        optimisticUpdate.isHeatmapMaximized =
          preferences.dashboardView === "HTMP";
      }

      // Apply optimistic update immediately
      set(optimisticUpdate);

      try {
        const client = getOctagonClient();
        const response = await client.updateUserPreference(userId, preferences);
        const data = response as Required<UserPreference>;

        // Confirm with server response
        const confirmedUpdate: Partial<WatchlistState> = {
          userPreferences: { ...userPreferences, ...data },
        };

        // Re-sync isComparisonMaximized with confirmed dashboardView
        if (preferences.dashboardView !== undefined) {
          confirmedUpdate.isComparisonMaximized = data.dashboardView === "COMP";
          confirmedUpdate.isHeatmapMaximized = data.dashboardView === "HTMP";
        }

        set(confirmedUpdate);
      } catch (error) {
        // On error, revert optimistic update
        set({
          userPreferences,
          error:
            error instanceof Error
              ? error.message
              : "Failed to update user preferences",
        });
      }
    },

    // UI state setters
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    setSortBy: (sortBy: WatchlistState["sortBy"]) => {
      set({ sortBy });
    },

    setSortDirection: (direction: "asc" | "desc") => {
      set({ sortDirection: direction });
    },

    setFilterQuery: (query: string) => {
      set({ filterQuery: query });
    },

    setSelectedSecurityTypes: (types: string[]) => {
      set({ selectedSecurityTypes: types });
    },

    setSelectedSectors: (sectors: string[]) => {
      set({ selectedSectors: sectors });
    },

    // Fetch comparison chart data
    fetchComparisonChart: async (chartRange?: ChartDateRange) => {
      const {
        userId,
        selectedPortfolioId,
        selectedChartRange,
        currentWatchlist,
      } = get();

      // Guard: Ensure session is fully initialized before fetching
      // Prevents premature requests before auth/JWT token is ready
      if (!userId || !selectedPortfolioId || !currentWatchlist) {
        // Don't fetch yet - session not ready
        // Clear loading state to prevent stuck UI
        set({
          comparisonChartLoading: false,
        });

        return;
      }

      const range = chartRange || selectedChartRange;

      // Special case: empty watchlist (no positions/lots)
      // Don't fetch from Octagon, use synthetic flat 0% data instead
      const hasNoPositions =
        !currentWatchlist.positions || currentWatchlist.positions.length === 0;

      set({
        comparisonChartLoading: true,
        comparisonChartError: null,
        selectedChartRange: range,
      });

      try {
        let data;

        if (hasNoPositions) {
          // Synthesize empty watchlist data with flat 0% series
          // This matches original behavior where empty watchlists show 0.00%
          data = {
            id: currentWatchlist.id,
            name: currentWatchlist.name,
            currency: currentWatchlist.currency,
            comparisons: [],
            chartData: [
              {
                id: currentWatchlist.id.toString(),
                name: currentWatchlist.name,
                issuedCurrency: currentWatchlist.currency,
                priceMovements: [],
              },
            ],
          };
        } else {
          // Fetch real data from Octagon for watchlists with positions
          const client = getOctagonClient();

          data = await client.getComparisonChart(
            userId,
            selectedPortfolioId.toString(),
            range
          );
        }

        if (!data) {
          set({
            comparisonChartError: "No comparison data available",
            comparisonChartLoading: false,
          });

          return;
        }

        // Initialize all legends as selected
        const legendIds = new Set(data.chartData?.map((t) => t.id) || []);

        set({
          comparisonData: data,
          selectedChartLegends: legendIds,
          comparisonChartLoading: false,
        });
      } catch (error) {
        set({
          comparisonChartError:
            error instanceof Error
              ? error.message
              : "Failed to fetch comparison chart",
          comparisonChartLoading: false,
        });
      }
    },

    // Fetch comparison settings (what's being compared)
    fetchComparisonSettings: async () => {
      const { userId, selectedPortfolioId } = get();

      if (!userId || !selectedPortfolioId) {
        return;
      }

      try {
        const client = getOctagonClient();
        const settings = await client.getComparisons(
          userId,
          selectedPortfolioId
        );

        set({ comparisonSettings: settings });
      } catch (error) {
        // Fail silently - comparison settings are optional
        set({ comparisonSettings: null });
      }
    },

    // Update comparison settings
    updateComparisonSettings: async (
      comparisons: WatchlistComparisonInfo["comparisons"]
    ) => {
      const { userId, selectedPortfolioId } = get();

      if (!userId || !selectedPortfolioId) {
        return;
      }

      try {
        const client = getOctagonClient();

        await client.updateComparisons(userId, selectedPortfolioId, {
          watchlistComparisons: comparisons,
        });

        // Refresh comparison settings and chart data
        await get().fetchComparisonSettings();
        await get().fetchComparisonChart();
      } catch (error) {
        set({
          comparisonChartError:
            error instanceof Error
              ? error.message
              : "Failed to update comparisons",
        });
      }
    },

    // Set chart date range
    setChartRange: (range: ChartDateRange) => {
      set({ selectedChartRange: range });
    },

    // Toggle a chart legend (show/hide ticker line)
    toggleChartLegend: (tickerId: string) => {
      const { selectedChartLegends } = get();
      const newLegends = new Set(selectedChartLegends);

      if (newLegends.has(tickerId)) {
        newLegends.delete(tickerId);
      } else {
        newLegends.add(tickerId);
      }

      set({ selectedChartLegends: newLegends });
    },

    // Set multiple selected legends at once
    setSelectedLegends: (tickerIds: string[]) => {
      set({ selectedChartLegends: new Set(tickerIds) });
    },

    // Clear comparison chart data
    clearComparisonChart: () => {
      set({
        comparisonData: null,
        comparisonSettings: null,
        selectedChartLegends: new Set(),
        comparisonChartError: null,
      });
    },

    // Toggle comparison chart maximized state
    toggleComparisonMaximized: () => {
      const { isComparisonMaximized } = get();
      const newDashboardView = isComparisonMaximized ? "DASH" : "COMP";

      get().updateUserPreferences({ dashboardView: newDashboardView });
    },

    // Toggle heatmap maximized state
    toggleHeatmapMaximized: () => {
      const { isHeatmapMaximized } = get();
      const newDashboardView = isHeatmapMaximized ? "DASH" : "HTMP";

      get().updateUserPreferences({ dashboardView: newDashboardView });
    },

    // Set heatmap tab view
    setHeatmapTab: (tab: "PERF" | "HOLD") => {
      set({ heatmapTabView: tab });
      get().updateUserPreferences({ webHeatmapView: tab });
    },

    openDrawer: (ticker: string) => {
      set({ isDrawerOpen: true, selectedTicker: ticker });
    },

    closeDrawer: () => {
      set({ isDrawerOpen: false, selectedTicker: null });
    },

    // Reset to initial state
    reset: () => {
      set(createInitialState());
    },
  }));
};

// Context type
type WatchlistStoreApi = ReturnType<typeof createWatchlistStore>;

export const WatchlistContext = createContext<WatchlistStoreApi | null>(null);

interface WatchlistProviderProps {
  children: ReactNode;
  initialState?: Partial<WatchlistState>;
}

export function WatchlistProvider({
  children,
  initialState,
}: Readonly<WatchlistProviderProps>) {
  // Create the Zustand store once using useRef to maintain stability
  const storeRef = useRef<WatchlistStoreApi>();

  if (!storeRef.current) {
    storeRef.current = createWatchlistStore(initialState);
  }

  return (
    <WatchlistContext.Provider value={storeRef.current}>
      {children}
    </WatchlistContext.Provider>
  );
}

// Base hook to access the store
function useWatchlistStore<T>(selector: (state: WatchlistStore) => T): T {
  const store = useContext(WatchlistContext);

  if (!store) {
    throw new Error("useWatchlistStore must be used within WatchlistProvider");
  }

  return useStore(store, selector);
}

// Hook to access actions (doesn't cause re-renders since actions are stable)
export function useWatchlistActions(): WatchlistActions {
  const store = useContext(WatchlistContext);

  if (!store) {
    throw new Error(
      "useWatchlistActions must be used within WatchlistProvider"
    );
  }

  // Return the actions directly from the store - these are stable function references
  // They won't change between renders as they're defined in the store creator
  const state = store.getState();

  return useMemo(
    () => ({
      initialize: state.initialize,
      fetchPortfolios: state.fetchPortfolios,
      selectPortfolio: state.selectPortfolio,
      refreshCurrentWatchlist: state.refreshCurrentWatchlist,
      saveWatchlistChanges: state.saveWatchlistChanges,
      deleteWatchlist: state.deleteWatchlist,
      fetchUserPreferences: state.fetchUserPreferences,
      updateUserPreferences: state.updateUserPreferences,
      setLoading: state.setLoading,
      setError: state.setError,
      setSortBy: state.setSortBy,
      setSortDirection: state.setSortDirection,
      setFilterQuery: state.setFilterQuery,
      setSelectedSecurityTypes: state.setSelectedSecurityTypes,
      setSelectedSectors: state.setSelectedSectors,
      fetchComparisonChart: state.fetchComparisonChart,
      fetchComparisonSettings: state.fetchComparisonSettings,
      updateComparisonSettings: state.updateComparisonSettings,
      setChartRange: state.setChartRange,
      toggleChartLegend: state.toggleChartLegend,
      setSelectedLegends: state.setSelectedLegends,
      clearComparisonChart: state.clearComparisonChart,
      toggleComparisonMaximized: state.toggleComparisonMaximized,
      toggleHeatmapMaximized: state.toggleHeatmapMaximized,
      setHeatmapTab: state.setHeatmapTab,
      openDrawer: state.openDrawer,
      closeDrawer: state.closeDrawer,
      reset: state.reset,
    }),
    [
      state.initialize,
      state.fetchPortfolios,
      state.selectPortfolio,
      state.refreshCurrentWatchlist,
      state.saveWatchlistChanges,
      state.deleteWatchlist,
      state.fetchUserPreferences,
      state.updateUserPreferences,
      state.setLoading,
      state.setError,
      state.setSortBy,
      state.setSortDirection,
      state.setFilterQuery,
      state.setSelectedSecurityTypes,
      state.setSelectedSectors,
      state.fetchComparisonChart,
      state.fetchComparisonSettings,
      state.updateComparisonSettings,
      state.setChartRange,
      state.toggleChartLegend,
      state.setSelectedLegends,
      state.clearComparisonChart,
      state.toggleComparisonMaximized,
      state.toggleHeatmapMaximized,
      state.setHeatmapTab,
      state.openDrawer,
      state.closeDrawer,
      state.reset,
    ]
  );
}

// Hooks for specific state slices - these will only re-render when their slice changes
export function useUserId() {
  return useWatchlistStore((state) => state.userId);
}

export function usePortfolios() {
  return useWatchlistStore((state) => state.portfolios);
}

export function useCurrentWatchlist() {
  return useWatchlistStore((state) => state.currentWatchlist);
}

export function useSelectedPortfolioId() {
  return useWatchlistStore((state) => state.selectedPortfolioId);
}

export function useUserPreferences() {
  return useWatchlistStore((state) => state.userPreferences);
}

export function useIsLoading() {
  return useWatchlistStore((state) => state.isLoading);
}

export function useError() {
  return useWatchlistStore((state) => state.error);
}

export function useSortBy() {
  return useWatchlistStore((state) => state.sortBy);
}

export function useSortDirection() {
  return useWatchlistStore((state) => state.sortDirection);
}

export function useFilterQuery() {
  return useWatchlistStore((state) => state.filterQuery);
}

export function useSelectedSecurityTypes() {
  return useWatchlistStore((state) => state.selectedSecurityTypes);
}

export function useSelectedSectors() {
  return useWatchlistStore((state) => state.selectedSectors);
}

export function useSectorData() {
  return useWatchlistStore((state) => state.sectorData);
}

// Comparison chart selectors
export function useComparisonData() {
  return useWatchlistStore((state) => state.comparisonData);
}

export function useComparisonSettings() {
  return useWatchlistStore((state) => state.comparisonSettings);
}

export function useChartRange() {
  return useWatchlistStore((state) => state.selectedChartRange);
}

export function useComparisonChartLoading() {
  return useWatchlistStore((state) => state.comparisonChartLoading);
}

export function useComparisonChartError() {
  return useWatchlistStore((state) => state.comparisonChartError);
}

export function useSelectedLegends() {
  return useWatchlistStore((state) => state.selectedChartLegends);
}

export function useIsComparisonMaximized() {
  return useWatchlistStore((state) => state.isComparisonMaximized);
}

// Heatmap selectors
export function useIsHeatmapMaximized() {
  return useWatchlistStore((state) => state.isHeatmapMaximized);
}

export function useHeatmapTabView() {
  return useWatchlistStore((state) => state.heatmapTabView);
}

export function useIsDrawerOpen() {
  return useWatchlistStore((state) => state.isDrawerOpen);
}

export function useSelectedTicker() {
  return useWatchlistStore((state) => state.selectedTicker);
}

// Applies text search, security type, and sector filters to positions.
// Uses useShallow to prevent re-renders when the filtered array is referentially new but structurally equal.
export function useFilteredPositions() {
  return useWatchlistStore(
    useShallow((state) => {
      const {
        currentWatchlist,
        filterQuery,
        selectedSecurityTypes,
        selectedSectors,
      } = state;

      if (!currentWatchlist?.positions) return [];

      let positions = currentWatchlist.positions;

      // Text search — matches ticker or company name (case-insensitive)
      if (filterQuery) {
        const query = filterQuery.toLowerCase();

        positions = positions.filter(
          (position) =>
            position.ticker.toLowerCase().includes(query) ||
            position.marketData.longName?.toLowerCase().includes(query)
        );
      }

      // Security type filter — maps raw security types to asset classes via SECURITY_TYPE_TO_ASSET_TYPE.
      // Skipped when empty or "All" is selected.
      if (
        selectedSecurityTypes.length > 0 &&
        !selectedSecurityTypes.includes("All")
      ) {
        const typeSet = new Set(selectedSecurityTypes);

        positions = positions.filter((position) => {
          const secType =
            position.marketData?.terminalSecurityType ||
            position.marketData?.securityType;

          if (!secType) return false;

          const assetType = SECURITY_TYPE_TO_ASSET_TYPE[secType] || secType;

          return typeSet.has(assetType);
        });
      }

      // Sector filter — matches against position's BICS sector.
      // Skipped when empty or "All" is selected.
      if (selectedSectors.length > 0 && !selectedSectors.includes("All")) {
        const sectorSet = new Set(selectedSectors);

        positions = positions.filter(
          (position) =>
            position.marketData?.sector !== undefined &&
            position.marketData.sector !== null &&
            sectorSet.has(position.marketData.sector)
        );
      }

      return positions;
    })
  );
}

export function useSortedPositions() {
  return useWatchlistStore(
    useShallow((state) => {
      const { currentWatchlist, sortBy, sortDirection, filterQuery } = state;

      if (!currentWatchlist?.positions) return [];

      let positions = [...currentWatchlist.positions];

      // Filter first
      if (filterQuery) {
        const query = filterQuery.toLowerCase();

        positions = positions.filter(
          (position) =>
            position.ticker.toLowerCase().includes(query) ||
            position.marketData.longName?.toLowerCase().includes(query)
        );
      }

      // Then sort
      positions.sort((a, b) => {
        switch (sortBy) {
          case "ticker": {
            const aVal = a.ticker;
            const bVal = b.ticker;

            return sortDirection === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
          case "name": {
            const aVal = a.marketData.longName || "";
            const bVal = b.marketData.longName || "";

            return sortDirection === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
          case "price": {
            const aVal = a.marketData.price || 0;
            const bVal = b.marketData.price || 0;

            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
          }
          case "change": {
            const aVal = a.marketData.priceChange1Day || 0;
            const bVal = b.marketData.priceChange1Day || 0;

            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
          }
          default:
            return 0;
        }
      });

      return positions;
    })
  );
}

// Filtered chart data based on legend selection
export function useFilteredChartData() {
  return useWatchlistStore((state) => {
    if (!state.comparisonData?.chartData) return [];

    const { chartData } = state.comparisonData;
    const { selectedChartLegends } = state;

    // If no legends selected, show all
    if (selectedChartLegends.size === 0) return chartData;

    // Filter to selected legends
    return chartData.filter((ticker) => selectedChartLegends.has(ticker.id));
  });
}

// Security type breakdown for donut chart
export function useSecurityTypeData() {
  return useWatchlistStore((state) => state.securityTypeData);
}

// Hook for complete state access (use sparingly - will re-render on any state change)
export function useWatchlistFullState(): WatchlistState {
  return useWatchlistStore((state) => state);
}
